import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Mouse, Keyboard, SlidersHorizontal, X, RotateCw, Grip, Wifi, WifiOff } from "lucide-react";
import { useLive } from "@/context/LiveContext";
import { usePcControl } from "./usePcControl";
import { PcTouchpad } from "./PcTouchpad";
import "./PcRemote.css";

const LETTERS = ["qwertyuiop", "asdfghjklñ", "zxcvbnm"];
const SYMBOLS = [["1","2","3","4","5","6","7","8","9","0"], ["@","#","€","$","%","&","*","(",")","/"], ["¿","?","¡","!",":",";","_","-","+", "="]];
const EXTRA = ["á","é","í","ó","ú","ü","[","]","{","}","\\","|","~","^",'"',"'","<",">",",","."];
const ALTGR = {"1":"|","2":"@","3":"#","4":"~","5":"€","6":"¬","7":"{","8":"[","9":"]","0":"}"};
const SHIFTED = {"1":"!","2":'"',"3":"·","4":"$","5":"%","6":"&","7":"/","8":"(","9":")","0":"="};
const SHORTCUTS = [["Win+D","d",["win"]],["Win+E","e",["win"]],["Alt+Tab","tab",["alt"]],["Ctrl+C","c",["ctrl"]],["Ctrl+V","v",["ctrl"]],["Ctrl+Z","z",["ctrl"]],["Ctrl+A","a",["ctrl"]],["Ctrl+S","s",["ctrl"]]];
const SPECIAL = [["Esc","escape"],["Tab","tab"],["Supr","delete"],["Ins","insert"],["Inicio","home"],["Fin","end"],["Re Pág","pageup"],["Av Pág","pagedown"],...Array.from({length:12},(_,i)=>["F"+(i+1),"f"+(i+1)])];

export function PcRemote() {
  const { onlineAgent } = useLive();
  const [open,setOpen] = useState(false), [tab,setTab] = useState("mouse");
  const [settings,setSettings] = useState(false), [symbols,setSymbols] = useState(false);
  const [mods,setMods] = useState([]), [caps,setCaps] = useState(false), [dragging,setDragging] = useState(false);
  const [text,setText] = useState(""), [notice,setNotice] = useState("");
  const [sensitivity,setSensitivity] = useState(() => {
    try { const n=Number(localStorage.getItem("jarvis.pc.sensitivity")); return n>=.5&&n<=3?n:1.5; } catch { return 1.5; }
  });
  const { ready,status,latency,send,move,reconnect } = usePcControl(open,onlineAgent?.id);
  const panel = useRef(null), opener = useRef(null);
  const closeRemote = useCallback(() => {
    send({ action: "release" });
    setDragging(false);
    setMods([]);
    setOpen(false);
  }, [send]);
  useEffect(() => {
    if (!ready) { setDragging(false); setMods([]); }
  },[ready]);
  useEffect(() => {
    if (!open) return;
    const previousOverflow=document.body.style.overflow;
    const returnFocus=opener.current;
    document.body.style.overflow="hidden";
    panel.current?.focus();
    const update = () => panel.current?.style.setProperty("--pc-viewport", (window.visualViewport?.height || window.innerHeight)+"px");
    update(); window.visualViewport?.addEventListener("resize",update);
    const keys = e => {
      if (e.key==="Escape") { e.preventDefault(); closeRemote(); }
      if (e.key==="Tab") {
        const elements=[...panel.current.querySelectorAll("button:not(:disabled),input:not(:disabled),[tabindex='0']")].filter(el=>el.getClientRects().length);
        const first=elements[0],last=elements[elements.length-1];
        if (e.shiftKey && document.activeElement===first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement===last) { e.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener("keydown",keys);
    return () => {
      document.body.style.overflow=previousOverflow;
      window.visualViewport?.removeEventListener("resize",update); window.removeEventListener("keydown",keys);
      returnFocus?.focus();
    };
  },[open, closeRemote]);
  const emit = event => { setNotice(""); return send(event); };
  const changeTab = next => {
    emit({action:"release"}); setDragging(false); setTab(next); setSettings(false);
  };
  const write = value => {
    if (mods.some(m=>["ctrl","win","alt"].includes(m))) {
      if (/^[a-z0-9]$/i.test(value)) return emit({action:"key",key:value.toLowerCase(),modifiers:mods});
      setNotice("Liberá Ctrl, Alt y Win para escribir ese símbolo."); return false;
    }
    const mapped = (mods.includes("altgr") && ALTGR[value]) || (mods.includes("shift") && SHIFTED[value]);
    const uppercase = caps !== mods.includes("shift");
    return emit({action:"text",text:mapped || (uppercase?value.toUpperCase():value)});
  };
  const key = name => emit({action:"key",key:name,modifiers:mods});
  const latch = m => setMods(old=>old.includes(m)?old.filter(x=>x!==m):[...old,m]);
  const startDrag = () => { if (emit({action:"drag",active:!dragging})) setDragging(!dragging); };
  return <section className="mt-3" data-testid="pc-remote">
    <button ref={opener} className="pc-open-button" aria-expanded={open} onClick={()=>{setOpen(true);setTab("mouse");setSettings(false);}}>
      <Mouse size={20}/><span>ABRIR CONTROL REMOTO DE PC</span><span aria-label={onlineAgent?.id ? "Agente Windows detectado" : "Agente Windows desconectado"} title={onlineAgent?.id ? "Agente Windows detectado" : "Agente Windows desconectado"}>{onlineAgent?.id ? "●" : "○"}</span>
    </button>
    {open && createPortal(<div className="pc-remote-backdrop">
      <div ref={panel} role="dialog" aria-modal="true" aria-label="Control remoto de PC" tabIndex={-1} className="pc-remote-panel">
        <header className="pc-remote-header">
          <div className="pc-remote-heading"><span className="pc-remote-brand">JARVIS / REMOTE</span><strong>{onlineAgent?.name || "TU PC WINDOWS"}</strong></div>
          <button className="pc-icon-button" aria-label="Cerrar" onClick={closeRemote}><X size={22}/></button>
        </header>
        <div className={"pc-connection"+(ready?" connected":"")}>
          {ready?<Wifi size={14}/>:<WifiOff size={14}/>}<span role="status">{status}</span>
          {ready && latency!==null && <small title="Tiempo de confirmación de Windows">{latency} ms</small>}
          {!ready && <button onClick={reconnect} className="pc-reconnect"><RotateCw size={13}/>Reconectar</button>}
        </div>
        <main className="pc-remote-main">
          {settings && <div className="pc-remote-settings">
            <label htmlFor="pc-sensitivity">Sensibilidad del cursor <strong>{sensitivity}×</strong></label>
            <input id="pc-sensitivity" aria-label="Sensibilidad" type="range" min=".5" max="3" step=".25" value={sensitivity}
              onChange={e=>{const n=Number(e.target.value);setSensitivity(n);try{localStorage.setItem("jarvis.pc.sensitivity",String(n));}catch{}}}/>
            <p>Controla la ventana activa de Windows. Las teclas se liberan al terminar cada combinación y el arrastre al cerrar. La sesión dura hasta 10 minutos.</p>
            {latency>250 && <p>Tu conexión tarda {latency} ms en confirmar. Esa demora de red también afecta al cursor.</p>}
          </div>}
          <fieldset disabled={!ready} className="pc-controls">
            {tab==="mouse" ? <div className="pc-mouse-surface">
              <PcTouchpad ready={ready} send={emit} move={move} sensitivity={sensitivity} dragging={dragging} setDragging={setDragging}/>
              <div className="pc-mouse-buttons">
                <button className="pc-mouse-click" aria-label="Clic izquierdo" onClick={()=>emit({action:"click",button:"left"})}><span>IZQUIERDO</span><Mouse size={22}/></button>
                <button className="pc-mouse-click" aria-label="Clic derecho" onClick={()=>emit({action:"click",button:"right"})}><span>DERECHO</span><Mouse size={22}/></button>
              </div>
              <div className="pc-mouse-actions">
                <button onClick={()=>emit({action:"click",button:"left",count:2})}>Doble clic</button>
                <button aria-pressed={dragging} onClick={startDrag}><Grip size={15}/>{dragging?"Soltar":"Arrastrar"}</button>
                {[["←",-1,0],["↑",0,1],["↓",0,-1],["→",1,0]].map(([label,dx,dy])=><button aria-label={"Scroll "+label} key={label} onClick={()=>emit({action:"scroll",dx,dy})}>{label}</button>)}
              </div>
            </div> : <div className="pc-keyboard">
              <div className="pc-modifiers">
                {["win","ctrl","alt","shift","altgr"].map(m=><button aria-pressed={mods.includes(m)} key={m} onClick={()=>latch(m)}>{m==="win"?"⊞ Win":m==="altgr"?"AltGr":m[0].toUpperCase()+m.slice(1)}</button>)}
                <button aria-label="Liberar modificadores" onClick={()=>{setMods([]);emit({action:"release"});}}>Liberar</button>
              </div>
              <p className="pc-keyboard-note">{mods.length?"Activos: "+mods.join(" + ").toUpperCase()+" · tocá una tecla":"Tocá un modificador para mantenerlo seleccionado"}</p>
              <div className="pc-key-special">{SPECIAL.map(([label,name])=><button key={name} onClick={()=>key(name)}>{label}</button>)}</div>
              <div className="pc-key-layout">
                {(symbols?SYMBOLS:LETTERS.map(row=>[...row])).map((row,i)=><div className={"pc-key-row row-"+i} key={i}>
                  {!symbols && i===2 && <button className="pc-key-wide" aria-label="Mayúsculas" aria-pressed={mods.includes("shift")} onClick={()=>latch("shift")}>⇧</button>}
                  {row.map(c=><button key={c} aria-label={c} onClick={()=>write(c)}>{mods.includes("altgr")&&ALTGR[c]?ALTGR[c]:mods.includes("shift")&&SHIFTED[c]?SHIFTED[c]:caps!==mods.includes("shift")?c.toUpperCase():c}</button>)}
                  {!symbols && i===2 && <button className="pc-key-wide" aria-label="Backspace" onClick={()=>key("backspace")}>⌫</button>}
                </div>)}
                <div className="pc-key-row">
                  <button className="pc-key-wide" onClick={()=>setSymbols(!symbols)}>{symbols?"ABC":"123"}</button>
                  <button aria-label="Bloq Mayús" aria-pressed={caps} onClick={()=>{if(key("capslock"))setCaps(!caps);}}>⇪</button>
                  <button onClick={()=>write(",")}>,</button>
                  <button className="pc-key-space" onClick={()=>key("space")}>Espacio</button>
                  <button onClick={()=>write(".")}>.</button>
                  <button className="pc-key-wide" onClick={()=>key("enter")}>Enter</button>
                </div>
              </div>
              <div className="pc-key-extra">{EXTRA.map(c=><button key={c} aria-label={c} onClick={()=>write(c)}>{caps!==mods.includes("shift")?c.toUpperCase():c}</button>)}</div>
              <div className="pc-key-navigation">
                <button onClick={()=>emit({action:"key",key:"win",modifiers:[]})}>⊞ Inicio</button>
                {[["←","left"],["↓","down"],["↑","up"],["→","right"]].map(([label,name])=><button aria-label={"Flecha "+name} key={name} onClick={()=>key(name)}>{label}</button>)}
                {symbols && <button aria-label="Backspace" onClick={()=>key("backspace")}>⌫</button>}
              </div>
              <div className="pc-shortcuts">{SHORTCUTS.map(([label,name,modifiers])=><button key={label} onClick={()=>emit({action:"key",key:name,modifiers})}>{label}</button>)}</div>
              <form className="pc-text-form" onSubmit={e=>{e.preventDefault();if(text&&emit({action:"text",text}))setText("");}}>
                <input aria-label="Escribir texto español" autoComplete="off" autoCorrect="off" autoCapitalize="none" maxLength={300} value={text} onChange={e=>setText(e.target.value)} placeholder="Escribir desde el teclado del iPhone…"/>
                <button disabled={!text}>Enviar</button>
              </form>
            </div>}
          </fieldset>
          {notice && <p role="alert" className="pc-notice">{notice}</p>}
        </main>
        <footer className="pc-remote-toolbar">
          <div role="tablist" aria-label="Modo de control">
            <button role="tab" aria-selected={tab==="mouse"} onClick={()=>changeTab("mouse")}><Mouse size={21}/>Mouse táctil</button>
            <button role="tab" aria-selected={tab==="keyboard"} onClick={()=>changeTab("keyboard")}><Keyboard size={21}/>Teclado Windows</button>
          </div>
          <button aria-label="Opciones del control remoto" aria-pressed={settings} onClick={()=>setSettings(!settings)}><SlidersHorizontal size={21}/><span>Opciones</span></button>
        </footer>
      </div>
    </div>,document.body)}
  </section>;
}
